import { DragEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Close, Save } from '@mui/icons-material';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	FormGroup,
	LinearProgress,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';

import useSuccessSnackbar from 'hooks/useSuccessSnakbar';

import { currency, enqueueSnackbarError } from 'lib/helpers';
import { createProductCategory, fetchProductCategory, updateProductCategory } from 'lib/models/productCategories';

import useProducts from 'hooks/useProducts';
import { updateProduct } from 'lib/models/products';
import ProductModal from 'components/ProductModal';
import useInAppAvailabilities from 'hooks/useInAppAvailabilities';
import useInAppAvailabilitiesActions from 'hooks/useInAppAvailabilitiesActions';
import { InAppSaleType } from 'lib/models/order';
import ConfirmationDialog from 'components/ConfirmationDialog';

type Props = {
	defaultValue: PopulatedProductCategory;
	onClose: () => void;
	refetch: () => void;
	forceClose?: boolean;
};

const search = {
	page: 0,
	// search all products in one page
	rowsPerPage: 50000,
	order: 'asc' as 'asc',
	orderBy: 'title',
};

export default function ProductCategoryModal({ defaultValue, onClose, refetch, forceClose }: Props) {
	const { t } = useTranslation();
	const successSnackbar = useSuccessSnackbar();

	const [loadingProductCategory, setLoading] = useState(false);
	const [productCategory, setProductCategory] = useState(defaultValue);
	const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

	const [showProductModal, setShowProductModal] = useState<Product | null>(null);

	const [searchValue, setSearch] = useState('');

	const { loading: loadingProducts, products: productsRaw, refetch: refetchProducts } = useProducts(search);
	const { availabilities, refetch: refetchAvailabilities } = useInAppAvailabilities();
	const { loading: loadingAvailability, updateAvailability, deleteAvailability } = useInAppAvailabilitiesActions();

	const [showBulkAvailabilityConfirm, setShowBulkAvailabilityConfirm] = useState<'add' | 'remove' | null>(null);
	const [bulkOperationProgress, setBulkOperationProgress] = useState<{ current: number; total: number } | null>(null);

	const loading = loadingProductCategory || loadingProducts || loadingAvailability;

	const handleDragStart = (index: number) => {
		setDraggingIndex(index);
	};

	const onProductModalClose = (product?: Product | null) => {
		refetch();
		if (product?._id) {
			onAdd(product);
		}
		setShowProductModal(null);
	};

	const products = productsRaw.filter(
		(product) => !productCategory._id || !product.categories.includes(productCategory._id)
	);

	const hasAvailability = (productId?: string) => {
		if (!productId) return false;
		return availabilities.some((av) => `${av.product}` === `${productId}`);
	};

	const handleDragOver = (event: DragEvent<HTMLTableRowElement>, index: number) => {
		event.preventDefault(); // Prevent the default behavior (like animation)

		if (draggingIndex === index || draggingIndex === null) return;

		const updatedItems = [...(productCategory.items || [])];
		const draggedItem = updatedItems[draggingIndex];

		// Remove dragged item and insert it at the new index
		updatedItems.splice(draggingIndex, 1);
		updatedItems.splice(index, 0, draggedItem);

		setDraggingIndex(index);
		setProductCategory((current) => ({ ...current, items: updatedItems }));
	};

	const handleDrop = () => {
		setDraggingIndex(null); // Clear dragging state
	};

	const onUpdate = (key: keyof ProductCategory) => {
		return (e: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = e.target;
			setProductCategory({ ...productCategory, [key]: value });
		};
	};

	const toggleVIP = () => {
		setProductCategory({ ...productCategory, vip: !productCategory.vip });
	};

	const refetchProductCategory = async () => {
		const data = await fetchProductCategory(`${productCategory._id}`);
		if (!!data) {
			const items = data.items;
			setProductCategory((current) => ({ ...current, items }));
		}
	};

	const onDelete = async (product: Product) => {
		const removed = await updateProduct({
			...product,
			categories: product.categories.filter((category) => category !== `${productCategory._id}`),
		});
		if (!removed) {
			enqueueSnackbarError('No se pudo quitar el producto de la categoría');
		}
		refetchProductCategory();
		refetch();
		refetchProducts();
	};

	const onAdd = async (product: Product) => {
		const added = await updateProduct({
			...product,
			categories: product.categories.includes(`${productCategory._id}`)
				? product.categories
				: [...product.categories, `${productCategory._id}`],
		});
		if (!added) {
			enqueueSnackbarError('No se pudo agregar el producto a la categoría');
		}
		refetchProductCategory();
		refetch();
		refetchProducts();
	};

	const save = async () => {
		setLoading(true);
		try {
			const data = await (productCategory?._id
				? updateProductCategory(productCategory)
				: createProductCategory(productCategory));
			if (data) {
				setTimeout(() => {
					setProductCategory((current) => ({ ...current, _id: data._id }));
				});
			}
			successSnackbar();
		} catch (error) {
			enqueueSnackbarError(error);
		}
		setLoading(false);

		if (productCategory._id || forceClose) {
			onClose();
		}
	};

	const handleBulkAvailabilityConfirm = async (confirmed: boolean) => {
		if (!confirmed || !showBulkAvailabilityConfirm) {
			setShowBulkAvailabilityConfirm(null);
			return;
		}

		try {
			if (showBulkAvailabilityConfirm === 'add') {
				// Add availability of type 'order' to all products in the category
				const productsToAdd = productCategory.items.filter(
					(product) => !availabilities.find((av) => `${av.product}` === `${product._id}`)
				);

				setBulkOperationProgress({ current: 0, total: productsToAdd.length });

				for (let i = 0; i < productsToAdd.length; i++) {
					const product = productsToAdd[i];
					setBulkOperationProgress({ current: i + 1, total: productsToAdd.length });

					await updateAvailability({
						product: `${product._id}`,
						dailyLimit: 0,
						monthlyLimit: 0,
						exceptions: [],
						type: InAppSaleType.order,
					});
				}
				successSnackbar();
			} else if (showBulkAvailabilityConfirm === 'remove') {
				// Remove availability for all products in the category
				const productsToRemove = productCategory.items.filter((product) =>
					availabilities.find((av) => `${av.product}` === `${product._id}`)
				);

				setBulkOperationProgress({ current: 0, total: productsToRemove.length });

				for (let i = 0; i < productsToRemove.length; i++) {
					const product = productsToRemove[i];
					const existingAvailability = availabilities.find((av) => `${av.product}` === `${product._id}`);

					setBulkOperationProgress({ current: i + 1, total: productsToRemove.length });

					if (existingAvailability?._id) {
						await deleteAvailability(existingAvailability._id);
					}
				}
				successSnackbar();
			}

			refetchAvailabilities();
			refetchProductCategory();
		} catch (error) {
			enqueueSnackbarError(error);
		} finally {
			setBulkOperationProgress(null);
			setShowBulkAvailabilityConfirm(null);
		}
	};

	const categoryHasAllAvailability = productCategory.items.every((item) =>
		availabilities.some((av) => `${av.product}` === `${item._id}`)
	);

	const categoryHasSomeAvailability = productCategory.items.some((item) =>
		availabilities.some((av) => `${av.product}` === `${item._id}`)
	);

	return (
		<Dialog open fullWidth={true} maxWidth="lg">
			{loading && <LinearProgress />}

			{showProductModal && <ProductModal defaultValue={showProductModal} onClose={onProductModalClose} />}

			{showBulkAvailabilityConfirm && (
				<ConfirmationDialog
					title={
						showBulkAvailabilityConfirm === 'add' ? 'Agregar disponibilidad masiva' : 'Eliminar disponibilidad masiva'
					}
					description={
						showBulkAvailabilityConfirm === 'add'
							? `Se agregará disponibilidad de tipo "COMPRA" a todos los productos de esta categoría que no tengan disponibilidad configurada. \n\nNOTA: No es posible agregar masivamente disponibilidad de tipo "RESERVA". Para productos que requieren reserva, debe configurarse individualmente desde el editor de cada producto.`
							: `Se eliminará la configuración de disponibilidad de TODOS los productos en esta categoría. Esta acción no se puede deshacer.`
					}
					onClose={handleBulkAvailabilityConfirm}
				/>
			)}

			<DialogTitle sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
				<div>{t('merch:merchandising')}</div>
				<div style={{ cursor: 'pointer' }} onClick={onClose}>
					X
				</div>
			</DialogTitle>

			<DialogContent sx={{ minWidth: '30vw' }}>
				{loading && <LinearProgress sx={{ mb: 3 }} />}
				{productCategory && (
					<FormGroup sx={{ paddingTop: 1 }}>
						<TextField
							id="title"
							label={t('common:title')}
							fullWidth={true}
							value={productCategory.title}
							variant="outlined"
							sx={{ mb: 3 }}
							onChange={onUpdate('title')}
							disabled={loading}
						/>
						<TextField
							id="title"
							multiline={true}
							label={t('common:description')}
							fullWidth={true}
							value={productCategory.description}
							variant="outlined"
							sx={{ mb: 3 }}
							onChange={onUpdate('description')}
							disabled={loading}
						/>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'row',
								justifyContent: 'flex-start',
								alignItems: 'center',
								gap: 2,
								ml: 1,
							}}
						>
							<Typography>VIP</Typography>
							<Checkbox
								id="vip"
								value={productCategory.vip}
								onChange={toggleVIP}
								disabled={loading}
								checked={!!productCategory.vip}
							/>
						</Box>

						{productCategory._id && productCategory.items.length > 0 && (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: 1,
									mt: 3,
									mb: 2,
									p: 2,
									backgroundColor: '#f5f5f5',
									borderRadius: 1,
								}}
							>
								<Typography variant="h6">Disponibilidad masiva</Typography>
								<Typography variant="caption" sx={{ color: 'gray' }}>
									Gestiona la disponibilidad para todos los productos de esta categoría
								</Typography>
								{bulkOperationProgress ? (
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: 2 }}>
										<CircularProgress />
										<Typography variant="body2">
											Procesando {bulkOperationProgress.current} de {bulkOperationProgress.total} productos...
										</Typography>
									</Box>
								) : (
									<Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
										<Button
											variant="outlined"
											color={categoryHasAllAvailability ? 'success' : 'primary'}
											disabled={loading || categoryHasAllAvailability}
											onClick={() => setShowBulkAvailabilityConfirm('add')}
											size="small"
										>
											{categoryHasAllAvailability
												? 'Todos tienen disponibilidad'
												: categoryHasSomeAvailability
												? 'Agregar a productos faltantes'
												: 'Agregar disponibilidad a todos'}
										</Button>
										<Button
											variant="outlined"
											color="error"
											disabled={loading || !categoryHasSomeAvailability}
											onClick={() => setShowBulkAvailabilityConfirm('remove')}
											size="small"
										>
											Eliminar disponibilidad de todos
										</Button>
									</Box>
								)}
								<Typography variant="caption" sx={{ color: 'orange', mt: 1 }}>
									⚠️ La disponibilidad masiva solo agrega tipo "COMPRA". Para configurar "RESERVA", edita cada producto
									individualmente.
								</Typography>
							</Box>
						)}

						{productCategory._id && (
							<Box>
								<Typography variant="h6">Productos</Typography>

								<Typography variant="caption" sx={{ color: 'red' }}>
									ADVERTENCIA: Esta pantalla (a diferencia de la pantalla de secciones o productos) guardará la
									selección de los productos automáticamente. guardado manual.
								</Typography>
								<div>
									<Typography variant="caption" sx={{ color: 'red' }}>
										ADVERTENCIA: La modificación del ORDEN de los productos necesita del guardado MANUAL.
									</Typography>
								</div>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell align="left" padding="normal">
												Título
											</TableCell>
											<TableCell align="left" padding="normal">
												Precio
											</TableCell>
											<TableCell align="center" padding="normal">
												Disponibilidad
											</TableCell>
											<TableCell align="left" padding="normal">
												Acciones
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{productCategory.items.map((item, index) => {
											return (
												<TableRow
													draggable={true}
													key={`product-table-${item._id}`}
													onDragStart={() => handleDragStart(index)}
													onDragOver={(ev) => handleDragOver(ev, index)}
													onDrop={handleDrop}
												>
													<TableCell align="left" padding="normal">
														<Box
															onClick={() => setShowProductModal(item)}
															style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
														>
															<Typography>
																{item.title}
																{item.code ? ` (${item.code})` : ''}
															</Typography>
															<Typography variant="caption">{item.description}</Typography>
														</Box>
													</TableCell>
													<TableCell align="left" padding="normal">
														<Box>{currency(item.price || 0)}</Box>
													</TableCell>
													<TableCell align="center" padding="normal">
														<Box>{hasAvailability(item._id) ? '✓' : '—'}</Box>
													</TableCell>
													<TableCell align="left" padding="normal">
														<Checkbox onChange={() => onDelete(item)} checked={true} />
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>

								<Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
									<Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
										Seleccionar producto
									</Typography>
									{!!productCategory._id && (
										<Button
											variant="contained"
											color="primary"
											size="large"
											sx={{ m: 1 }}
											onClick={() =>
												setShowProductModal({
													categories: [`${productCategory._id}`],
													price: 0,
													title: '',
												})
											}
											disabled={!productCategory.title}
										>
											Crear producto
										</Button>
									)}
								</Box>

								<TextField
									fullWidth
									value={searchValue}
									label={'Buscar'}
									onChange={(ev) => setSearch(ev.target.value)}
								/>

								<Table>
									<TableHead>
										<TableRow>
											<TableCell align="left" padding="normal">
												Título
											</TableCell>
											<TableCell align="left" padding="normal">
												Precio
											</TableCell>
											<TableCell align="center" padding="normal">
												Disponibilidad
											</TableCell>
											<TableCell align="left" padding="normal">
												Acciones
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{products.filter(filterProducts(normalizeText(searchValue))).map((item) => {
											return (
												<TableRow key={`product-table-select-${item._id}`}>
													<TableCell align="left" padding="normal">
														<Box onClick={() => setShowProductModal(item)} style={{ cursor: 'pointer' }}>
															<Typography>
																{item.title}
																{item.code ? ` (${item.code})` : ''}
															</Typography>
															<Typography variant="caption">{item.description}</Typography>
														</Box>
													</TableCell>
													<TableCell align="left" padding="normal">
														<Box>{currency(item.price || 0)}</Box>
													</TableCell>
													<TableCell align="center" padding="normal">
														<Box>{hasAvailability(item._id) ? '✓' : '—'}</Box>
													</TableCell>
													<TableCell align="left" padding="normal">
														<Checkbox onChange={() => onAdd(item)} checked={false} />
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</Box>
						)}

						<Button
							variant="contained"
							color="primary"
							size="large"
							sx={{ m: 1 }}
							startIcon={<Save />}
							onClick={save}
							disabled={!productCategory.title}
						>
							{t('common:save')}
						</Button>

						<Button
							variant="contained"
							color="secondary"
							size="large"
							sx={{ m: 1 }}
							startIcon={<Close />}
							onClick={onClose}
						>
							{t('common:cancel')}
						</Button>
					</FormGroup>
				)}
			</DialogContent>
		</Dialog>
	);
}

function normalizeText(text: string): string {
	return text
		.normalize('NFD') // Decomposes characters (e.g., "é" → "é")
		.replace(/[\u0300-\u036f]/g, '') // Removes the diacritic marks while keeping base letters
		.toLowerCase();
}

function filterProducts(searchValue: string) {
	const normalizedSearch = normalizeText(searchValue);

	return (p: Product) => {
		return normalizeText(p.title).includes(normalizedSearch);
	};
}
